import React, { useState, useEffect } from 'react';
import ProjectChoicesCard from './settings/ProjectChoicesCard';
import MenuSettingsCard from './settings/MenuSettingsCard';
import LoadErrorBox from './ui/LoadErrorBox';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from './Icon';
import TrashPanel from './settings/TrashPanel';
import { Input as FormInput } from './FormField';
import ConfirmDialog from './ui/ConfirmDialog';
import { useNotifications } from './NotificationProvider';
import SearchableSelect from './ui/SearchableSelect';
import { loadEvents } from '../services/EventsService';
import { useSettings } from '../contexts/SettingsContext';
import { useRole } from '../hooks/useRole';
import { useAuth } from '../contexts/AuthContext';
import { errorMessageOf } from '../utils/apiErrors';
import {
  RelationshipLabel,
  loadRelationshipLabels,
  refreshRelationshipLabelsFromApi,
  createRelationshipLabelApi,
  deleteRelationshipLabelApi,
} from '../services/RelationshipLabelsService';
import { loadSettingsFromDb, saveSettingsToDb } from '../services/SettingsService';
import WorkspaceSettingsPanel from './settings/WorkspaceSettingsPanel';
import WorkspaceDangerZone from './settings/WorkspaceDangerZone';
import TableSetupModal from './event-day/TableSetupModal';
import BotGroupsSettings from './BotGroupsSettings';
import TagMultiSelect from './ui/TagMultiSelect';
import StatusBadge from './ui/StatusBadge';
// Honoree feature archived in ans-backend, UI disabled 2026-09-23
import '../styles/components/Settings.css';

// General Settings
interface GeneralSettings {
  theme: 'light' | 'dark' | 'auto';
  autoSave: boolean;
  autoSaveInterval: number; // minutes
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
}

// Project Settings — global project config only.
// Per-event dates and venues are managed in the Events section.
export interface PaymentMethodConfig {
  bankName: string;
  accountNumber: string;
  accountName: string;
  mobileMoney?: string;
  reference?: string;
}

interface ProjectSettings {
  coupleNames: string;
  brideName: string;
  groomName: string;
  hashtag: string;
  rsvpDeadline: string;
  guestCapacity: number;
  paymentMethod?: PaymentMethodConfig;
  weddingWebsite: {
    enabled: boolean;
    url: string;
    passwordProtected: boolean;
    password?: string;
  };
}

// Guest Settings
interface GuestSettings {
  defaultRSVPStatus: 'pending' | 'confirmed' | 'declined';
  allowPlusOnes: boolean;
  maxPlusOnes: number;
  requirePhone: boolean;
  requireEmail: boolean;
  guestCategories: string[];
  seatingChart: {
    enabled: boolean;
    autoAssign: boolean;
  };
  communications: {
    welcomeMessage: string;
    reminderDays: number[];
    followUpDays: number[];
  };
}

// Vendor Settings
interface VendorSettings {
  defaultCategories: string[];
  paymentTerms: {
    depositPercentage: number;
    finalPaymentDays: number;
  };
  contractTemplates: string[];
  vendorRatings: {
    enabled: boolean;
    criteria: string[];
  };
  budgetAlerts: {
    enabled: boolean;
    threshold: number; // percentage
  };
}

// Communications Settings
interface CommunicationsSettings {
  emailProvider: 'gmail' | 'outlook' | 'custom';
  smtpSettings?: {
    host: string;
    port: number;
    username: string;
    password: string;
  };
  smsProvider: 'twilio' | 'africastalking' | 'custom';
  twilioSettings?: {
    accountSid: string;
    authToken: string;
    fromNumber: string;
  };
  smsSettings?: {
    apiKey: string;
    senderId: string;
  };
  whatsapp: {
    enabled: boolean;
    templates: string[];
    // Channel invite link shared with RSVP-confirmed guests (#371 — "Auto-add
    // RSVP-confirmed guests to the couple's WhatsApp channel"). v1 scope is
    // the planner triggering a per-guest "Add to WhatsApp channel" action
    // (see GuestProfile/GuestsList) that opens a wa.me deep link pre-filled
    // with this link — not full auto-send. Persisted at
    // comms_config.whatsapp.channelLink.
    channelLink?: string;
    // WhatsApp Business messaging (Gupshup) is not part of settings: its details live encrypted in the provider
    // keys and are entered in Communications (ProviderPanel).
  };
  templates: {
    rsvpConfirmation: string;
    paymentReminder: string;
    weddingUpdate: string;
    thankYou: string;
  };
}

// Advanced Settings
interface AdvancedSettings {
  export: {
    formats: ('pdf' | 'excel' | 'csv')[];
    includeImages: boolean;
    compression: boolean;
  };
  integrations: {
    googleCalendar: boolean;
    quickbooks: boolean;
    salesforce: boolean;
    customWebhook?: string;
  };
  security: {
    twoFactorAuth: boolean;
    sessionTimeout: number; // minutes
    ipWhitelist: string[];
  };
}

// Master Settings Interface
interface MasterSettings {
  general: GeneralSettings;
  project: ProjectSettings;
  guests: GuestSettings;
  vendors: VendorSettings;
  communications: CommunicationsSettings;
  advanced: AdvancedSettings;
}

type SettingsTab = 'account' | 'general' | 'project' | 'workspace' | 'guests' | 'vendors' | 'communications' | 'event-day' | 'trash' | 'advanced';

export interface SettingsActions {
  save: () => void;
  reset: () => void;
  hasUnsavedChanges: boolean;
  saving: boolean;
}

interface SettingsProps {
  onActionsReady?: (actions: SettingsActions) => void;
}

// Controls that look real but save nothing (two-factor sign-in, session timeout, export formats,
// integrations, IP allow-list). Hidden, not deleted: they come back as real workspace policies
// (see alfa2267/ans-backend#321). A fake security toggle is worse than none. Billing, vendor
// categories and WhatsApp live on the workspace Settings page.
const SHOW_UNBUILT_CONTROLS = false;

const TAB_URL_MAP: Record<string, SettingsTab> = {
  'account': 'account', 'general': 'general',
  'project': 'project', 'workspace': 'workspace', 'guests': 'guests', 'vendors': 'vendors',
  'communications': 'communications', 'event-day': 'event-day',
  'trash': 'trash', 'advanced': 'advanced',
  // legacy redirects (old bookmarks and links keep working)
  'wedding': 'project',
  'bot': 'communications',
  'inbox': 'communications',
  'asoebi-delivery': 'project',
};
const TAB_TO_URL: Record<SettingsTab, string> = {
  'account': 'account', 'general': 'general',
  'project': 'project', 'workspace': 'workspace', 'guests': 'guests', 'vendors': 'vendors',
  'communications': 'communications', 'event-day': 'event-day',
  'trash': 'trash', 'advanced': 'advanced',
};

const Settings: React.FC<SettingsProps> = ({ onActionsReady }) => {
  const { settings: contextSettings, updateSettings: contextUpdateSettings } = useSettings();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState<MasterSettings | null>(null);
  const [loading, setLoading] = useState(true);
  // A failed load is shown with Retry. Showing a form full of defaults instead
  // would let a Save overwrite the project's real settings.
  const [loadError, setLoadError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Relationship labels (guest-to-guest relations vocabulary) — a separate
  // catalog from guest tags above: these are the phrases offered in the
  // relations picker on a guest's profile ("Father of", "Plus one of"), not
  // attributes stamped onto a guest record. Backed by ans-backend's
  // relationship_labels table (RelationshipLabelsService).
  const [relationshipLabelsList, setRelationshipLabelsList] = useState<RelationshipLabel[]>(() => loadRelationshipLabels());
  useEffect(() => {
    void refreshRelationshipLabelsFromApi().then(setRelationshipLabelsList);
  }, []);
  const [newRelationLabel, setNewRelationLabel] = useState('');
  const handleAddRelationLabel = async () => {
    if (!newRelationLabel.trim()) return;
    try {
      await createRelationshipLabelApi(newRelationLabel.trim());
      setRelationshipLabelsList(loadRelationshipLabels());
      setNewRelationLabel('');
      showSuccess('Relationship label added.');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add relationship label.');
    }
  };
  const handleRemoveRelationLabel = async (id: string) => {
    try {
      await deleteRelationshipLabelApi(id);
      setRelationshipLabelsList(loadRelationshipLabels());
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove relationship label.');
    }
  };

  const getTabFromUrl = (): SettingsTab => {
    const path = location.pathname.replace('/settings', '').replace(/^\//, '');
    const tab = TAB_URL_MAP[path] || 'account';
    return tab === 'advanced' && !SHOW_UNBUILT_CONTROLS ? 'account' : tab;
  };
  const [activeTab, setActiveTabState] = useState<SettingsTab>(getTabFromUrl);
  const setActiveTab = (tab: SettingsTab) => {
    setActiveTabState(tab);
    navigate(`/settings/${TAB_TO_URL[tab]}`, { replace: true });
  };
  useEffect(() => { setActiveTabState(getTabFromUrl()); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-user account settings (stored in localStorage per email)
  const accountStorageKey = user?.email ? `anojeje_account_${user.email}` : null;
  const [accountName, setAccountName] = useState(user?.name || '');
  const [account2FA, setAccount2FA] = useState(false);
  const [accountSessionTimeout, setAccountSessionTimeout] = useState(120);

  // Team directory (/team) is disabled in the API, so the display name comes
  // straight from the signed-in user.
  useEffect(() => {
    if (user?.name) setAccountName(user.name);
  }, [user?.name]);

  useEffect(() => {
    if (!accountStorageKey) return;
    try {
      const stored = JSON.parse(localStorage.getItem(accountStorageKey) || '{}');
      if (stored.twoFactorAuth !== undefined) setAccount2FA(stored.twoFactorAuth);
      if (stored.sessionTimeout !== undefined) setAccountSessionTimeout(stored.sessionTimeout);
    } catch { /* ignore */ }
  }, [accountStorageKey]);

  const saveAccountSettings = () => {
    if (!accountStorageKey) return;
    localStorage.setItem(accountStorageKey, JSON.stringify({
      displayName: accountName,
      twoFactorAuth: account2FA,
      sessionTimeout: accountSessionTimeout,
    }));
  };

  useEffect(() => {
    loadSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync from SettingsContext whenever the remote profile loads or changes
  useEffect(() => {
    if (loading) return;
    setSettings(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        general: {
          ...prev.general,
          theme: contextSettings.theme,
          autoSave: contextSettings.project.autoSave,
          notifications: { ...contextSettings.notifications },
        },
        project: {
          ...prev.project,
          coupleNames: contextSettings.project.coupleNames || prev.project.coupleNames,
          brideName: contextSettings.project.brideName || prev.project.brideName,
          groomName: contextSettings.project.groomName || prev.project.groomName,
          hashtag: contextSettings.project.hashtag || prev.project.hashtag,
          rsvpDeadline: contextSettings.project.rsvpDeadline || prev.project.rsvpDeadline,
        },
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextSettings]);

  const loadSettings = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const dbSettings = await loadSettingsFromDb();

      const defaultSettings: MasterSettings = {
        general: {
          theme: contextSettings.theme,
          autoSave: contextSettings.project.autoSave,
          autoSaveInterval: 5,
          notifications: { ...contextSettings.notifications },
        },
        // The service already fills any block the project has not saved yet.
        project: {
          ...dbSettings.project,
          // Couple names and the like may also live in the profile; the project's saved value wins.
          coupleNames: dbSettings.project.coupleNames || contextSettings.project.coupleNames,
          brideName: dbSettings.project.brideName || contextSettings.project.brideName,
          groomName: dbSettings.project.groomName || contextSettings.project.groomName,
          hashtag: dbSettings.project.hashtag || contextSettings.project.hashtag,
          rsvpDeadline: dbSettings.project.rsvpDeadline || contextSettings.project.rsvpDeadline,
          // Only this project's own saved bank details. Never a browser-wide copy: that showed another
          // project's account number to a project that had none.
          paymentMethod: dbSettings.project.paymentMethod,
        },
        guests: dbSettings.guests,
        vendors: dbSettings.vendors,
        communications: dbSettings.communications,
        advanced: {
          export: {
            formats: ['pdf', 'excel'],
            includeImages: false,
            compression: true,
          },
          integrations: {
            googleCalendar: false,
            quickbooks: false,
            salesforce: false,
          },
          security: {
            twoFactorAuth: false,
            sessionTimeout: 120,
            ipWhitelist: [],
          },
        },
      };
      setSettings(defaultSettings);
    } catch (error) {
      console.error('Failed to load settings:', error instanceof Error ? error.message : 'unknown error');
      setLoadError(error ?? new Error('load failed'));
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = (section: keyof MasterSettings, updates: any) => {
    if (!settings) return;
    
    setSettings(prev => prev ? {
      ...prev,
      [section]: { ...prev[section], ...updates }
    } : null);
    setHasUnsavedChanges(true);
  };

  const saveSettings = async () => {
    if (!hasUnsavedChanges) return;

    setSaving(true);
    try {
      saveAccountSettings();
      if (!settings) { setSaving(false); setHasUnsavedChanges(false); return; }

      // Save project/vendor/guest/comms settings to the API. Currency, timezone,
      // language and phone country are saved by the Project settings card, and no
      // credential is ever sent (the service strips them).
      await saveSettingsToDb({
        project: settings.project,
        guests: settings.guests,
        vendors: settings.vendors,
        communications: settings.communications,
      });

      // Persist overlapping fields to SettingsContext
      contextUpdateSettings({
        theme: settings.general.theme,
        notifications: settings.general.notifications,
        project: {
          ...contextSettings.project,
          coupleNames: settings.project.coupleNames,
          rsvpDeadline: settings.project.rsvpDeadline,
          autoSave: settings.general.autoSave,
          brideName: settings.project.brideName,
          groomName: settings.project.groomName,
          hashtag: settings.project.hashtag,
        },
      });

      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
      showError(errorMessageOf(error, "Couldn't save the settings. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const [confirmReset, setConfirmReset] = useState(false);

  const resetSettings = () => {
    setConfirmReset(true);
  };

  const doResetSettings = () => {
    setConfirmReset(false);
    loadSettings();
    setHasUnsavedChanges(false);
  };

  // Expose actions to parent for breadcrumb bar buttons
  useEffect(() => {
    onActionsReady?.({ save: saveSettings, reset: resetSettings, hasUnsavedChanges, saving });
  }, [hasUnsavedChanges, saving, onActionsReady]);

  const { isOwner } = useRole();

  // Shared, project-level profile fields (couple_display_name/their_story/
  // quirks) — one row per project in ans-backend's project profile table, kept
  // separate from the settings.project object above because they persist
  // Project profile ("About the Couple") feature archived in ans-backend, UI disabled 2026-09-23

  const allTabs = [
    { id: 'account' as SettingsTab, label: 'My Account', icon: 'user', ownerOnly: false },
    { id: 'general' as SettingsTab, label: 'General', icon: 'settings', ownerOnly: false },
    { id: 'project' as SettingsTab, label: 'Project', icon: 'calendar', ownerOnly: false },
    { id: 'workspace' as SettingsTab, label: 'Workspace', icon: 'building', ownerOnly: true },
    { id: 'guests' as SettingsTab, label: 'Guests', icon: 'users', ownerOnly: false },
    { id: 'vendors' as SettingsTab, label: 'Vendors', icon: 'briefcase', ownerOnly: false },
    { id: 'communications' as SettingsTab, label: 'Communications', icon: 'message', ownerOnly: false },
    { id: 'event-day' as SettingsTab, label: 'Event Day', icon: 'calendar', ownerOnly: false },
    { id: 'trash' as SettingsTab, label: 'Trash', icon: 'trash', ownerOnly: false },
    { id: 'advanced' as SettingsTab, label: 'Advanced', icon: 'shield', ownerOnly: true },
  ];
  const tabs = allTabs.filter(t => (!t.ownerOnly || isOwner) && (t.id !== 'advanced' || SHOW_UNBUILT_CONTROLS));

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="loading-spinner" />
        <p>Loading settings...</p>
      </div>
    );
  }

  if (loadError !== null || !settings) {
    return (
      <div className="settings-container">
        <LoadErrorBox what="settings" error={loadError ?? undefined} onRetry={() => { void loadSettings(); }} />
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-layout">
        {/* Tab Navigation */}
        <div className="toolbar-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`toolbar-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="settings-main">
            {/* My Account */}
            {activeTab === 'account' && (
              <div className="settings-section">
                <div className="settings-card">
                  <h4 className="settings-card-title">Profile</h4>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={accountName}
                        disabled
                        className="settings-input-disabled settings-input-readonly"
                        readOnly
                      />
                      <span className="form-help-text settings-help-text">Managed by your Team profile</span>
                    </div>
                    <FormInput
                      label="Email"
                      type="email"
                      value={user?.email || ''}
                      disabled
                      help="Managed by your account"
                      className="settings-input-disabled"
                    />
                  </div>
                  {user?.picture && (
                    <div className="settings-avatar-row">
                      <img src={user.picture} alt="" className="settings-avatar" />
                    </div>
                  )}
                </div>

                {SHOW_UNBUILT_CONTROLS && (
                <div className="settings-card">
                  <h4 className="settings-card-title">Security</h4>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input type="checkbox" checked={account2FA} onChange={(e) => { setAccount2FA(e.target.checked); setHasUnsavedChanges(true); }} />
                        Two-Factor Authentication
                      </label>
                    </div>
                    <div className="form-group">
                      <label>Session Timeout (minutes)</label>
                      <input type="number" min="15" max="480" value={accountSessionTimeout} onChange={(e) => { setAccountSessionTimeout(parseInt(e.target.value) || 120); setHasUnsavedChanges(true); }} />
                    </div>
                  </div>
                </div>
                )}

                <div className="settings-card">
                  <h4 className="settings-card-title">Authentication</h4>
                  <p className="settings-auth-note">
                    {user?.picture
                      ? 'You are signed in via Google. Password management is handled through your Google account.'
                      : 'You are signed in with email and password. You can change your password by contacting your account owner.'}
                  </p>
                </div>

                <div className="settings-card"><h4 className="settings-card-title">Notifications</h4><div className="checkbox-group"><label className="checkbox-label"><input type="checkbox" checked={settings.general.notifications.email} onChange={(e) => updateSettings('general', { notifications: { ...settings.general.notifications, email: e.target.checked } })} /> Email</label><label className="checkbox-label"><input type="checkbox" checked={settings.general.notifications.push} onChange={(e) => updateSettings('general', { notifications: { ...settings.general.notifications, push: e.target.checked } })} /> Push</label><label className="checkbox-label"><input type="checkbox" checked={settings.general.notifications.sms} onChange={(e) => updateSettings('general', { notifications: { ...settings.general.notifications, sms: e.target.checked } })} /> SMS</label></div></div>
              </div>
            )}

            {/* General Settings */}
            {activeTab === 'general' && (
              <div className="settings-section">
                <div className="settings-card">
                  <h4 className="settings-card-title">Display</h4>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label className="form-label">Theme</label>
                      <SearchableSelect
                        value={settings.general.theme}
                        onChange={value => updateSettings('general', { theme: value })}
                        options={[
                          { value: 'light', label: 'Light' },
                          { value: 'dark', label: 'Dark' },
                          { value: 'auto', label: 'Auto' },
                        ]}
                      />
                    </div>
                  </div>
                </div>

                <MenuSettingsCard />

                <div className="settings-card">
                  <h4 className="settings-card-title">Auto-Save</h4>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input type="checkbox" checked={settings.general.autoSave} onChange={(e) => updateSettings('general', { autoSave: e.target.checked })} />
                        Enable Auto-Save
                      </label>
                    </div>
                    <div className="form-group">
                      <label>Interval (minutes)</label>
                      <input type="number" min="1" max="60" value={settings.general.autoSaveInterval} onChange={(e) => updateSettings('general', { autoSaveInterval: parseInt(e.target.value) })} disabled={!settings.general.autoSave} />
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* Project Settings */}
            {activeTab === 'project' && (
              <div className="settings-section">
                <ProjectChoicesCard />
                <div className="settings-card">
                  <div className="settings-grid">
                    <div className="form-group">
                      <label>Hashtag</label>
                      <input type="text" value={settings.project.hashtag} onChange={(e) => updateSettings('project', { hashtag: e.target.value })} placeholder="#YourHashtag" />
                    </div>
                  </div>
                </div>

                <div className="settings-card">
                  <h4 className="settings-card-title">Payment Method</h4>
                  <p className="settings-payment-hint">
                    Bank details used for guest asoebi payments and vendor deposits. Auto-populates the portal payment instructions.
                  </p>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label>Bank Name</label>
                      <input type="text" value={settings.project.paymentMethod?.bankName || ''} onChange={(e) => updateSettings('project', { paymentMethod: { ...settings.project.paymentMethod, bankName: e.target.value, accountNumber: settings.project.paymentMethod?.accountNumber || '', accountName: settings.project.paymentMethod?.accountName || '' } })} placeholder="Bank name" />
                    </div>
                    <div className="form-group">
                      <label>Account Name</label>
                      <input type="text" value={settings.project.paymentMethod?.accountName || ''} onChange={(e) => updateSettings('project', { paymentMethod: { ...settings.project.paymentMethod, bankName: settings.project.paymentMethod?.bankName || '', accountNumber: settings.project.paymentMethod?.accountNumber || '', accountName: e.target.value } })} placeholder="Name on the account" />
                    </div>
                    <div className="form-group">
                      <label>Account Number</label>
                      <input type="text" value={settings.project.paymentMethod?.accountNumber || ''} onChange={(e) => updateSettings('project', { paymentMethod: { ...settings.project.paymentMethod, bankName: settings.project.paymentMethod?.bankName || '', accountName: settings.project.paymentMethod?.accountName || '', accountNumber: e.target.value } })} placeholder="Account number" />
                    </div>
                    <div className="form-group">
                      <label>Reference Instructions</label>
                      <input type="text" value={settings.project.paymentMethod?.reference || ''} onChange={(e) => updateSettings('project', { paymentMethod: { ...settings.project.paymentMethod, bankName: settings.project.paymentMethod?.bankName || '', accountNumber: settings.project.paymentMethod?.accountNumber || '', accountName: settings.project.paymentMethod?.accountName || '', reference: e.target.value } })} placeholder="e.g. Use your full name as reference" />
                    </div>
                  </div>
                </div>

                {isOwner && <WorkspaceDangerZone />}
              </div>
            )}

            {/* Guests Settings */}
            {activeTab === 'guests' && (
              <div className="settings-section">
                <div className="settings-card">
                  <h4 className="settings-card-title">RSVP Defaults</h4>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label>Default Status</label>
                      <SearchableSelect
                        value={settings.guests.defaultRSVPStatus}
                        onChange={value => updateSettings('guests', { defaultRSVPStatus: value })}
                        options={[
                          { value: 'pending', label: 'Pending' },
                          { value: 'confirmed', label: 'Confirmed' },
                          { value: 'declined', label: 'Declined' },
                        ]}
                      />
                    </div>
                    <div className="form-group">
                      <label>Max Plus Ones</label>
                      <input type="number" min="0" max="10" value={settings.guests.maxPlusOnes} onChange={(e) => updateSettings('guests', { maxPlusOnes: parseInt(e.target.value) })} />
                    </div>
                  </div>
                  <div className="checkbox-group">
                    <label className="checkbox-label"><input type="checkbox" checked={settings.guests.allowPlusOnes} onChange={(e) => updateSettings('guests', { allowPlusOnes: e.target.checked })} /> Allow Plus Ones</label>
                    <label className="checkbox-label"><input type="checkbox" checked={settings.guests.requirePhone} onChange={(e) => updateSettings('guests', { requirePhone: e.target.checked })} /> Require Phone</label>
                    <label className="checkbox-label"><input type="checkbox" checked={settings.guests.requireEmail} onChange={(e) => updateSettings('guests', { requireEmail: e.target.checked })} /> Require Email</label>
                  </div>
                </div>

                <div className="settings-card">
                  <h4 className="settings-card-title">Relationship Labels</h4>
                  <p className="settings-muted-xs">
                    Manage the relationship phrases offered when linking guests to each other (e.g. "Father of", "Plus one of") on a guest's Relations section. A one-off phrase can still be typed there even if it isn't in this list.
                  </p>

                  <div className="settings-tag-section">
                    <div className="settings-tag-pills">
                      {relationshipLabelsList.map(label => (
                        <StatusBadge key={label.id} color="#6b7280">
                          {label.label}
                          <button onClick={() => handleRemoveRelationLabel(label.id)} className="settings-tag-remove-btn" title="Remove">&times;</button>
                        </StatusBadge>
                      ))}
                      {relationshipLabelsList.length === 0 && (
                        <span className="settings-muted-xs">No relationship labels yet.</span>
                      )}
                    </div>
                  </div>

                  <div className="settings-new-tag-row">
                    <div className="form-group settings-new-tag-name">
                      <label>New Relationship Label</label>
                      <input
                        type="text"
                        value={newRelationLabel}
                        onChange={e => setNewRelationLabel(e.target.value)}
                        placeholder="e.g. Uncle of"
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddRelationLabel(); } }}
                      />
                    </div>
                    <button className="btn-primary settings-add-tag-btn" onClick={handleAddRelationLabel}>Add</button>
                  </div>
                </div>

              </div>
            )}

            {/* Vendors Settings */}
            {activeTab === 'vendors' && (
              <div className="settings-section">
                <div className="settings-card">
                  <h4 className="settings-card-title">Templates</h4>
                  <p className="settings-card-hint">Vendor categories are shared by every project and are set in Workspace settings.</p>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label>Contract Templates</label>
                      <TagMultiSelect
                        values={settings.vendors.contractTemplates}
                        onChange={(v) => updateSettings('vendors', { contractTemplates: v })}
                        options={['Standard', 'Premium', 'Basic', 'Custom']}
                        placeholder="Add contract templates…"
                      />
                    </div>
                  </div>
                </div>

                <div className="settings-card">
                  <h4 className="settings-card-title">Payment Terms</h4>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label>Deposit Percentage (%)</label>
                      <input type="number" min="0" max="100" value={settings.vendors.paymentTerms.depositPercentage} onChange={(e) => updateSettings('vendors', { paymentTerms: { ...settings.vendors.paymentTerms, depositPercentage: parseInt(e.target.value) } })} />
                    </div>
                    <div className="form-group">
                      <label>Final Payment Days Before Event</label>
                      <input type="number" min="0" max="365" value={settings.vendors.paymentTerms.finalPaymentDays} onChange={(e) => updateSettings('vendors', { paymentTerms: { ...settings.vendors.paymentTerms, finalPaymentDays: parseInt(e.target.value) } })} />
                    </div>
                  </div>
                </div>

                <div className="settings-two-col">
                  <div className="settings-card">
                    <h4 className="settings-card-title">Vendor Ratings</h4>
                    <div className="form-group">
                      <label className="checkbox-label"><input type="checkbox" checked={settings.vendors.vendorRatings.enabled} onChange={(e) => updateSettings('vendors', { vendorRatings: { ...settings.vendors.vendorRatings, enabled: e.target.checked } })} /> Enable Vendor Ratings</label>
                    </div>
                  </div>
                  <div className="settings-card">
                    <h4 className="settings-card-title">Budget Alerts</h4>
                    <div className="settings-grid">
                      <div className="form-group">
                        <label className="checkbox-label"><input type="checkbox" checked={settings.vendors.budgetAlerts.enabled} onChange={(e) => updateSettings('vendors', { budgetAlerts: { ...settings.vendors.budgetAlerts, enabled: e.target.checked } })} /> Enable Budget Alerts</label>
                      </div>
                      {settings.vendors.budgetAlerts.enabled && (
                        <div className="form-group">
                          <label>Alert Threshold (%)</label>
                          <input type="number" min="50" max="100" value={settings.vendors.budgetAlerts.threshold} onChange={(e) => updateSettings('vendors', { budgetAlerts: { ...settings.vendors.budgetAlerts, threshold: parseInt(e.target.value) } })} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {settings.vendors.vendorRatings.enabled && (
                  <div className="settings-card settings-card-spaced">
                    <h4 className="settings-card-title">Rating Criteria</h4>
                    <div className="form-group">
                      <TagMultiSelect
                        values={settings.vendors.vendorRatings.criteria}
                        onChange={(v) => updateSettings('vendors', { vendorRatings: { ...settings.vendors.vendorRatings, criteria: v } })}
                        options={['Quality', 'Price', 'Professionalism', 'Timeliness', 'Communication', 'Creativity', 'Value for Money']}
                        placeholder="Add rating criteria…"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Communications Settings */}
            {activeTab === 'communications' && (
              <div className="settings-section">
                <div className="settings-card" role="note">
                  <h4 className="settings-card-title">Sign-in details are not saved here yet</h4>
                  <p className="settings-hint-text">
                    Passwords, tokens and API keys for e-mail, SMS and WhatsApp can't be stored in settings. They will be
                    kept encrypted as provider keys in a later step, so those fields are switched off and nothing typed
                    into them is saved. Everything else on this tab saves as usual.
                  </p>
                </div>

                <div className="settings-card">
                  <div className="form-group">
                    <label>Welcome Message</label>
                    <textarea value={settings.guests.communications.welcomeMessage} onChange={(e) => updateSettings('guests', { communications: { ...settings.guests.communications, welcomeMessage: e.target.value } })} rows={3} placeholder="Welcome! We are so excited to celebrate with you." />
                  </div>
                  <div className="settings-grid">
                    <div className="form-group">
                      <label>Reminder Days Before Event</label>
                      <TagMultiSelect
                        values={settings.guests.communications.reminderDays.map(String)}
                        onChange={(v) => updateSettings('guests', { communications: { ...settings.guests.communications, reminderDays: v.map(Number).filter(n => !isNaN(n) && n > 0) } })}
                        options={['1', '2', '3', '5', '7', '14', '21', '30']}
                        placeholder="Add reminder days…"
                        createLabel="Add day"
                      />
                    </div>
                    <div className="form-group">
                      <label>Follow-up Days After Event</label>
                      <TagMultiSelect
                        values={settings.guests.communications.followUpDays.map(String)}
                        onChange={(v) => updateSettings('guests', { communications: { ...settings.guests.communications, followUpDays: v.map(Number).filter(n => !isNaN(n) && n > 0) } })}
                        options={['1', '2', '3', '5', '7', '14']}
                        placeholder="Add follow-up days…"
                        createLabel="Add day"
                      />
                    </div>
                  </div>
                </div>

                <div className="settings-card">
                  {/* Email row: provider + address + password inline */}
                  <div className="settings-grid settings-email-row">
                    <div className="form-group">
                      <label>Email Provider</label>
                      <SearchableSelect
                        value={settings.communications.emailProvider}
                        onChange={value => updateSettings('communications', { emailProvider: value })}
                        options={[
                          { value: 'gmail', label: 'Gmail' },
                          { value: 'outlook', label: 'Outlook' },
                          { value: 'custom', label: 'Custom SMTP' },
                        ]}
                      />
                    </div>
                    {settings.communications.emailProvider === 'gmail' && <>
                      <div className="form-group"><label>Gmail Address</label><input type="email" value={settings.communications.smtpSettings?.username || ''} onChange={(e) => updateSettings('communications', { smtpSettings: { host: 'smtp.gmail.com', port: 587, username: e.target.value, password: settings.communications.smtpSettings?.password || '' } })} placeholder="yourname@gmail.com" /></div>
                      <div className="form-group"><label>Gmail App Password <span className="settings-gmail-app-password-hint">(<a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-primary">generate</a>)</span></label><input type="password" disabled value={settings.communications.smtpSettings?.password || ''} onChange={(e) => updateSettings('communications', { smtpSettings: { host: 'smtp.gmail.com', port: 587, username: settings.communications.smtpSettings?.username || '', password: e.target.value } })} placeholder="xxxx xxxx xxxx xxxx" /></div>
                    </>}
                    {settings.communications.emailProvider === 'outlook' && <>
                      <div className="form-group"><label>Outlook Address</label><input type="email" value={settings.communications.smtpSettings?.username || ''} onChange={(e) => updateSettings('communications', { smtpSettings: { host: 'smtp.office365.com', port: 587, username: e.target.value, password: settings.communications.smtpSettings?.password || '' } })} placeholder="yourname@outlook.com" /></div>
                      <div className="form-group"><label>Password</label><input type="password" disabled value={settings.communications.smtpSettings?.password || ''} onChange={(e) => updateSettings('communications', { smtpSettings: { host: 'smtp.office365.com', port: 587, username: settings.communications.smtpSettings?.username || '', password: e.target.value } })} /></div>
                    </>}
                    {settings.communications.emailProvider === 'custom' && <>
                      <div className="form-group"><label>SMTP Host</label><input type="text" value={settings.communications.smtpSettings?.host || ''} onChange={(e) => updateSettings('communications', { smtpSettings: { ...settings.communications.smtpSettings, host: e.target.value } })} /></div>
                      <div className="form-group"><label>SMTP Port</label><input type="number" value={settings.communications.smtpSettings?.port || 587} onChange={(e) => updateSettings('communications', { smtpSettings: { ...settings.communications.smtpSettings, port: parseInt(e.target.value) } })} /></div>
                      <div className="form-group"><label>Username</label><input type="text" value={settings.communications.smtpSettings?.username || ''} onChange={(e) => updateSettings('communications', { smtpSettings: { ...settings.communications.smtpSettings, username: e.target.value } })} /></div>
                      <div className="form-group"><label>Password</label><input type="password" disabled value={settings.communications.smtpSettings?.password || ''} onChange={(e) => updateSettings('communications', { smtpSettings: { ...settings.communications.smtpSettings, password: e.target.value } })} /></div>
                    </>}
                  </div>
                  {/* SMS row */}
                  <div className="settings-grid settings-email-row settings-email-row-spaced">
                    <div className="form-group">
                      <label>SMS Provider</label>
                      <SearchableSelect
                        value={settings.communications.smsProvider}
                        onChange={value => updateSettings('communications', { smsProvider: value })}
                        options={[
                          { value: 'twilio', label: 'Twilio' },
                          { value: 'africastalking', label: "Africa's Talking" },
                          { value: 'custom', label: 'Custom' },
                        ]}
                      />
                    </div>
                    {settings.communications.smsProvider === 'twilio' && <>
                      <div className="form-group"><label>Account SID <span className="settings-gmail-app-password-hint">(<a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-primary">Twilio Console</a>)</span></label><input type="text" value={settings.communications.twilioSettings?.accountSid || ''} onChange={(e) => updateSettings('communications', { twilioSettings: { ...settings.communications.twilioSettings, accountSid: e.target.value, authToken: settings.communications.twilioSettings?.authToken || '', fromNumber: settings.communications.twilioSettings?.fromNumber || '' } })} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" /></div>
                      <div className="form-group"><label>Auth Token</label><input type="password" disabled value={settings.communications.twilioSettings?.authToken || ''} onChange={(e) => updateSettings('communications', { twilioSettings: { ...settings.communications.twilioSettings, accountSid: settings.communications.twilioSettings?.accountSid || '', authToken: e.target.value, fromNumber: settings.communications.twilioSettings?.fromNumber || '' } })} /></div>
                    </>}
                    {settings.communications.smsProvider === 'africastalking' && <>
                      <div className="form-group"><label>API Key</label><input type="password" disabled value={settings.communications.smsSettings?.apiKey || ''} onChange={(e) => updateSettings('communications', { smsSettings: { apiKey: e.target.value, senderId: settings.communications.smsSettings?.senderId || '' } })} /></div>
                      <div className="form-group"><label>Sender ID</label><input type="text" value={settings.communications.smsSettings?.senderId || ''} onChange={(e) => updateSettings('communications', { smsSettings: { apiKey: settings.communications.smsSettings?.apiKey || '', senderId: e.target.value } })} placeholder="ANOJEJE" /></div>
                    </>}
                  </div>
                  {settings.communications.smsProvider === 'twilio' && (
                    <div className="settings-grid settings-email-row settings-email-row-spaced">
                      <div className="form-group"><label>From Number</label><input type="tel" value={settings.communications.twilioSettings?.fromNumber || ''} onChange={(e) => updateSettings('communications', { twilioSettings: { ...settings.communications.twilioSettings, accountSid: settings.communications.twilioSettings?.accountSid || '', authToken: settings.communications.twilioSettings?.authToken || '', fromNumber: e.target.value } })} placeholder="Number with country code" /></div>
                    </div>
                  )}
                </div>

                <div className="settings-card">
                  <div className="settings-grid">
                    <div className="form-group"><label>RSVP Confirmation</label><textarea value={settings.communications.templates.rsvpConfirmation} onChange={(e) => updateSettings('communications', { templates: { ...settings.communications.templates, rsvpConfirmation: e.target.value } })} rows={2} /></div>
                    <div className="form-group"><label>Payment Reminder</label><textarea value={settings.communications.templates.paymentReminder} onChange={(e) => updateSettings('communications', { templates: { ...settings.communications.templates, paymentReminder: e.target.value } })} rows={2} /></div>
                    <div className="form-group"><label>Project Update</label><textarea value={settings.communications.templates.weddingUpdate} onChange={(e) => updateSettings('communications', { templates: { ...settings.communications.templates, weddingUpdate: e.target.value } })} rows={2} /></div>
                    <div className="form-group"><label>Thank You Message</label><textarea value={settings.communications.templates.thankYou} onChange={(e) => updateSettings('communications', { templates: { ...settings.communications.templates, thankYou: e.target.value } })} rows={2} /></div>
                  </div>
                </div>

                <div className="settings-card">
                  <div className={`settings-whatsapp-toggle-row${settings.communications.whatsapp.enabled ? ' settings-whatsapp-toggle-row-spaced' : ''}`}>
                    <label className="checkbox-label settings-checkbox-label-fixed">
                      <input
                        type="checkbox"
                        checked={settings.communications.whatsapp.enabled}
                        onChange={(e) => updateSettings('communications', { whatsapp: { ...settings.communications.whatsapp, enabled: e.target.checked } })}
                      />
                      Enable WhatsApp
                    </label>
                    {settings.communications.whatsapp.enabled && (
                      <button
                        className="primary-btn bgs-add-btn settings-whatsapp-test-btn"
                        onClick={() => window.dispatchEvent(new CustomEvent('bgs-open-add-form'))}
                      >
                        + Add WhatsApp Group
                      </button>
                    )}
                  </div>
                  {settings.communications.whatsapp.enabled && (
                    <div className="form-group settings-whatsapp-channel-link-group">
                      <label>WhatsApp Channel Invite Link</label>
                      <input
                        type="url"
                        className="form-input"
                        value={settings.communications.whatsapp.channelLink ?? ''}
                        onChange={(e) => updateSettings('communications', { whatsapp: { ...settings.communications.whatsapp, channelLink: e.target.value } })}
                        placeholder="https://chat.whatsapp.com/…"
                      />
                      <p className="settings-hint-text">
                        Shared with RSVP-confirmed guests via the "Add to WhatsApp channel" action on their guest profile.
                      </p>
                    </div>
                  )}
                  {settings.communications.whatsapp.enabled && <BotGroupsSettings />}
                </div>

                {/* WhatsApp Business messaging (Gupshup) has one home: Communications, where the details are
                    stored encrypted (provider keys). They used to be entered here as well, into plain settings. */}
                <div className="settings-card">
                  <p className="settings-section-description">
                    WhatsApp Business messaging (Gupshup) is set up in <strong>Communications</strong> (admins only),
                    where the details are stored encrypted.
                  </p>
                </div>
              </div>
            )}

            {/* Event Day Setup */}
            {activeTab === 'event-day' && (
              <div className="settings-section">
                <TableSetupModal inline onClose={() => {}} onSaved={() => {}} />
              </div>
            )}

            {activeTab === 'workspace' && !isOwner && (
              <div className="auth-required">Only the owner can access Workspace Settings.</div>
            )}
            {activeTab === 'workspace' && isOwner && <WorkspaceSettingsPanel />}

            {/* Advanced Settings — owner only */}
            {activeTab === 'trash' && <TrashPanel />}

            {activeTab === 'advanced' && !isOwner && (
              <div className="auth-required">Only the owner can access Advanced Settings.</div>
            )}
            {SHOW_UNBUILT_CONTROLS && activeTab === 'advanced' && isOwner && (
              <div className="settings-section">
                <div className="settings-card">
                  <h4 className="settings-card-title">Export</h4>
                  <div className="checkbox-group">
                    <label className="checkbox-label"><input type="checkbox" checked={settings.advanced.export.formats.includes('pdf')} onChange={(e) => { const formats = e.target.checked ? [...settings.advanced.export.formats, 'pdf'] : settings.advanced.export.formats.filter(f => f !== 'pdf'); updateSettings('advanced', { export: { ...settings.advanced.export, formats } }); }} /> PDF</label>
                    <label className="checkbox-label"><input type="checkbox" checked={settings.advanced.export.formats.includes('excel')} onChange={(e) => { const formats = e.target.checked ? [...settings.advanced.export.formats, 'excel'] : settings.advanced.export.formats.filter(f => f !== 'excel'); updateSettings('advanced', { export: { ...settings.advanced.export, formats } }); }} /> Excel</label>
                    <label className="checkbox-label"><input type="checkbox" checked={settings.advanced.export.formats.includes('csv')} onChange={(e) => { const formats = e.target.checked ? [...settings.advanced.export.formats, 'csv'] : settings.advanced.export.formats.filter(f => f !== 'csv'); updateSettings('advanced', { export: { ...settings.advanced.export, formats } }); }} /> CSV</label>
                  </div>
                  <div className="checkbox-group settings-checkbox-group-top">
                    <label className="checkbox-label"><input type="checkbox" checked={settings.advanced.export.includeImages} onChange={(e) => updateSettings('advanced', { export: { ...settings.advanced.export, includeImages: e.target.checked } })} /> Include Images</label>
                    <label className="checkbox-label"><input type="checkbox" checked={settings.advanced.export.compression} onChange={(e) => updateSettings('advanced', { export: { ...settings.advanced.export, compression: e.target.checked } })} /> Compress Files</label>
                  </div>
                </div>

                <div className="settings-card">
                  <h4 className="settings-card-title">Integrations</h4>
                  <div className="checkbox-group">
                    <label className="checkbox-label"><input type="checkbox" checked={settings.advanced.integrations.googleCalendar} onChange={(e) => updateSettings('advanced', { integrations: { ...settings.advanced.integrations, googleCalendar: e.target.checked } })} /> Google Calendar</label>
                    <label className="checkbox-label"><input type="checkbox" checked={settings.advanced.integrations.quickbooks} onChange={(e) => updateSettings('advanced', { integrations: { ...settings.advanced.integrations, quickbooks: e.target.checked } })} /> QuickBooks</label>
                    <label className="checkbox-label"><input type="checkbox" checked={settings.advanced.integrations.salesforce} onChange={(e) => updateSettings('advanced', { integrations: { ...settings.advanced.integrations, salesforce: e.target.checked } })} /> Salesforce CRM</label>
                  </div>
                </div>

                <div className="settings-card settings-card-overflow-visible">
                  <h4 className="settings-card-title">Access Control</h4>
                  <div className="form-group">
                    <label>IP Whitelist</label>
                    <TagMultiSelect
                      values={settings.advanced.security.ipWhitelist}
                      onChange={(v) => updateSettings('advanced', { security: { ...settings.advanced.security, ipWhitelist: v } })}
                      options={[]}
                      placeholder="Add IP address…"
                      createLabel="Add IP"
                    />
                  </div>
                </div>
              </div>
            )}

        </div>
      </div>
      <ConfirmDialog
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={doResetSettings}
        title="Reset all settings?"
        description="Are you sure you want to reset all settings to default values?"
        confirmLabel="Reset"
        variant="primary"
      />
    </div>
  );
};

export default Settings;
